<script lang="ts" setup>
import {menuMainItems} from "~~/route/route.config";
import {ChevronLeftIcon} from "@heroicons/vue/24/outline";

const {collectionUri, pageUri} = useI18nCollection();
const {t, locale} = useI18n();
const router = useRouter();
const {giftCardData, setAmount, setRecipient, saveToLocalStorage} = useGiftCard();
const {getAllButIndex} = usePage();
const img = useImage();
const url = useRequestURL();
const currentUrl = `${url.protocol}//${url.host}`;
const routeConfig = menuMainItems;
const page = await queryCollection(collectionUri('giftcard'))
        .path(pageUri('giftcard'))
        .first();
const coverImage: string = (page.meta?.cover_image || '').replace('/assets', '');
const cardImage: string = (page.meta?.cover_image || '').replace('/assets', '');

// Fetch services to get their prices
const servicesPages = await queryCollection(collectionUri('services')).all();
const services = getAllButIndex(servicesPages);

// Map of amount -> service title
const servicesByAmount = computed(() => {
    const map = new Map<number, string>();
    services.forEach(service => {
        const price = service.meta?.price as string;
        if (!price) return;
        // Extract numeric value from price (e.g., "75€" -> 75)
        const match = price.match(/(\d+)/);
        if (match) {
            const amount = parseInt(match[1], 10);
            map.set(amount, service.title);
        }
    });
    return map;
});

const selectedAmount = ref<number>(giftCardData.value.amount || 0);
const customAmount = ref<string>('');
const recipientFromName = ref(giftCardData.value.recipient?.from_name || '');
const recipientToName = ref(giftCardData.value.recipient?.to_name || '');
const recipientMessage = ref(giftCardData.value.recipient?.message || '');

const getServiceTitle = (amount: number): string | undefined => {
    return servicesByAmount.value.get(amount);
};

const selectPredefinedAmount = (amount: number) => {
    selectedAmount.value = amount;
    customAmount.value = '';
};

const isFormValid = computed(() => {
    if (selectedAmount.value <= 0) return false;
    return (
            recipientFromName.value.trim() !== '' &&
            recipientToName.value.trim() !== '');
});

const handleCustomAmountInput = () => {
    const value = parseFloat(customAmount.value);
    if (!isNaN(value) && value > 0) {
        selectedAmount.value = value;
    } else {
        selectedAmount.value = 0;
    }
};

const handleSubmit = () => {
    if (!isFormValid.value) return;
    // Save data to state
    setAmount(selectedAmount.value);
    setRecipient({
        from_name: recipientFromName.value,
        to_name: recipientToName.value,
        message: recipientMessage.value,
    });
    saveToLocalStorage();
    router.push(routeConfig.find(i => i.identifier === 'gift-card-billing-info-form')?.path?.[locale.value] || '/');
};
useSeoMeta({
    title: page.meta!.seoTitle as string,
    ogTitle: page.meta!.seoTitle as string,
    description: page.meta!.seoDescription as string,
    ogDescription: page.meta!.seoDescription as string,
    ogImage: currentUrl + img(cardImage, {
        width: 1200,
        height: 630,
        fit: 'cover',
        format: 'jpg',
    }),
    twitterCard: 'summary_large_image',
});

onMounted(() => {
    const localStorageData = window.localStorage.getItem('clemkerato-giftcard');
    if (localStorageData) {
        const parsedData = JSON.parse(localStorageData);
        if (parsedData.amount) {
            selectedAmount.value = parsedData.amount;
            // If it's not a predefined amount, set it in custom amount field
            const predefinedAmounts = page.meta?.gift_card_amounts || [];
            if (!predefinedAmounts.includes(parsedData.amount)) {
                customAmount.value = parsedData.amount.toString();
            }
        }
        if (parsedData.recipient) {
            recipientFromName.value = parsedData.recipient.from_name || '';
            recipientToName.value = parsedData.recipient.to_name || '';
            recipientMessage.value = parsedData.recipient.message || '';
        }
    }
});
</script>

<template>
  <div>
    <NuxtLayout
          name="layout-with-background"
          :img-uri="coverImage"
          :fade-slow="true"
  >
    <article class="max-w-3xl mx-auto py-12">
      <NuxtLink
              :to="routeConfig.find(i => i.identifier === 'home')?.path?.[locale] || '/'"
              class="inline-flex items-center text-brand-dark mb-6 hover:underline opacity-66 text-sm"
      >
        <ChevronLeftIcon class="size-5 me-2" />
        {{ t('action.back_home') }}
      </NuxtLink>
      <header class="mb-8 max-w-2xl mx-auto">
        <h1 class="hero-title text-center mb-2 text-brand">
          {{ page.title }}
        </h1>
        <ContentRenderer :value="page" class="text-brand/66 text-center"/>
      </header>

      <form class="bg-brand-light p-8 rounded-lg shadow-md" @submit.prevent="handleSubmit">
        <div class="mb-8">
          <h2 class="text-xl font-semibold text-brand-dark mb-4">
            {{ t('gift-card.select-amount') }}
          </h2>
          <div class="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div
                    v-for="amount in page.meta?.gift_card_amounts || []"
                    :key="amount"
                    class="flex flex-col"
            >
              <button
                      type="button"
                      :class="[
                'py-3 px-6 rounded-lg font-medium transition-all cursor-pointer',
                selectedAmount === amount && customAmount === ''
                  ? 'bg-secondary text-white'
                  : 'bg-white text-brand-dark hover:bg-secondary hover:text-white border border-grey'
              ]"
                      @click="selectPredefinedAmount(amount)"
              >
                {{ amount }}€
              </button>
              <span 
                v-if="servicesByAmount.get(amount)" 
                class="text-xs text-brand/66 text-center mt-1 italic"
              >
                {{ servicesByAmount.get(amount) }}
              </span>
            </div>
          </div>
          
          <!-- Custom amount input -->
          <div class="mt-4">
            <label class="block text-sm font-medium text-brand-dark mb-2">
              {{ t('gift-card.custom-amount.label') }}
            </label>
            <div class="flex gap-2 items-center">
              <input
                v-model="customAmount"
                type="number"
                min="1"
                step="1"
                :placeholder="t('gift-card.custom-amount.placeholder')"
                class="flex-1 px-4 py-3 rounded-lg border border-grey focus:border-secondary focus:ring-2 focus:ring-secondary/20 outline-none"
                @input="handleCustomAmountInput"
              />
              <span class="text-brand-dark font-medium">€</span>
            </div>
          </div>
        </div>

        <Transition name="slide-fade">
          <div v-if="selectedAmount > 0" class="mb-8">
            <h3 class="text-lg font-semibold text-brand-dark mb-4">{{ t('gift-card.info')}}</h3>

            <FormInput
                    v-model="recipientFromName"
                    :label="t('gift-card.from-name.label')"
                    :placeholder="t('gift-card.from-name.placeholder')"
                    required
            />

            <FormInput
                    v-model="recipientToName"
                    :label="t('gift-card.to-name.label')"
                    :placeholder="t('gift-card.to-name.placeholder')"
                    required
            />

            <FormTextarea
                    v-model="recipientMessage"
                    :label="t('gift-card.message.label')"
                    :placeholder="t('gift-card.message.placeholder')"
                    :rows="4"
            />

            <!-- Submit Button -->
            <div class="text-center">
              <button
                      type="submit"
                      :disabled="!isFormValid"
                      :class="[
                'btn btn-secondary',
                !isFormValid ? 'opacity-50 cursor-not-allowed' : ''
              ]"
              >
                {{ t('gift-card.proceed-to-checkout') }}
              </button>
            </div>
          </div>
        </Transition>
      </form>
    </article>
    <div v-if="coverImage" class="hidden" aria-hidden="true">
      <media-background :img-uri="coverImage" :lazy="false"/>
    </div>
  </NuxtLayout>
  </div>
</template>
